"use client"

import { Crown, Coins } from "lucide-react"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { useUIStore } from "@/shared/stores/ui-store"
import { useAuthStore } from "@/features/auth/store"
import { useCoinBalance } from "@/features/credits/hooks/use-credit-balance"
import { isPaidTier, LOW_BALANCE_THRESHOLD } from "@/features/credits/constants"
import { ROUTES } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip"
import { useLocale, useTranslations } from "next-intl"
import { getIntlLocale } from "@/shared/lib/i18n/config"

/**
 * Coins / Premium widget for the topbar.
 * - Free users: a Sora-spec "Premium" pill — hairline + transparent + bold
 *   text. No orange/amber/rose gradient, no shimmer, no pulse.
 * - Paid users: a subtle coin-balance pill with a hairline edge. Low-balance
 *   state uses the destructive accent instead of amber.
 */
export function CoinsDisplay() {
  const t = useTranslations("credits.display")
  const locale = useLocale()
  const intlLocale = getIntlLocale(locale)
  const user = useAuthStore((s) => s.user)
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const { coins, isLoading } = useCoinBalance()

  if (!user) return null

  const tier = user.subscription
  const isPaid = isPaidTier(tier)

  // ── Free user: Premium 70% Off pill (Sora-spec) ──────────────────────
  if (!isPaid) {
    return (
      <LocalizedLink
        href={ROUTES.subscription}
        className={cn(
          // Premium CTA — Pleasur-blue accent border + subtle tinted wash signals
          // "this is the upgrade action" without slipping into amber/orange slop.
          "group flex items-center gap-1 sm:gap-2 h-7 sm:h-9 rounded-full px-2 sm:px-4",
          "text-xs sm:text-sm font-semibold text-foreground shrink-0",
          "border border-accent-blue/40 bg-accent-blue/[0.08] hover:bg-accent-blue/[0.14]",
          "transition-colors duration-150"
        )}
      >
        <Crown className="size-3 sm:size-3.5 text-accent-blue shrink-0" />
        <span className="whitespace-nowrap">
          <span className="hidden sm:inline">{t("premium")} </span>70%<span className="hidden sm:inline"> {t("off")}</span>
        </span>
      </LocalizedLink>
    )
  }

  // ── Paid user: Coins badge (Sora-spec) ───────────────────────────────
  // Low-balance uses the destructive accent (semantic warning); healthy
  // state uses the standard subtle surface.
  const isLow = !isLoading && coins <= LOW_BALANCE_THRESHOLD
  const formattedCoins = coins.toLocaleString(intlLocale)

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setActiveModal("buy-credits-paywall")}
            className={cn(
              "group flex items-center gap-1 rounded-full px-2 sm:px-3.5 py-1.5 text-xs sm:text-sm font-semibold tabular-nums tracking-tight sm:gap-1.5",
              "min-w-[4.25rem] sm:min-w-[5.25rem] transition-colors duration-150",
              "border",
              isLow
                ? "border-destructive/40 bg-destructive/[0.08] text-destructive hover:bg-destructive/[0.12]"
                : "border-border bg-foreground/[0.04] text-foreground hover:bg-foreground/[0.08]"
            )}
            aria-label={isLoading ? t("loadingCoins") : t("coinsRemaining", { count: formattedCoins })}
          >
            <Coins
              className={cn(
                "size-3.5 sm:size-4 shrink-0",
                isLow ? "text-destructive" : "text-foreground/80"
              )}
              aria-hidden
            />
            {isLoading ? (
              <span className="h-3.5 w-[2.25rem] rounded bg-foreground/[0.08] animate-pulse inline-block sm:w-[2.75rem]" aria-hidden />
            ) : (
              <span className="text-xs sm:text-sm">{formattedCoins}</span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[220px] text-center">
          <p className="font-medium text-foreground">{t("coinBalance")}</p>
          <p className="text-muted-foreground text-[11px] leading-snug mt-1">
            {t("coinBalanceDescription")}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
